import { useState, useEffect } from "react";
import {
  adminGetDiscountCodes,
  adminCreateDiscountCode,
  adminUpdateDiscountCode,
  adminDeleteDiscountCode,
} from "../../../../api/apiEndpoints";
import { formatCurrency } from "../../../../utils/partnerMetrics";
import {
  SectionTitle, Card, Row, Input, Select, Button, Table, Th, Td, Badge, FormGrid, Label, FormField, StatusText, ModalOverlay, Modal, ModalHeader, ModalTitle, CloseButton, EmptyState,
} from "../styles";

const CODES_PER_PAGE = 15;

const CITY_OPTIONS = ["Miami", "Fort Lauderdale", "New York", "Tampa"];
const PRICE_OPTIONS = [75, 119, 199];

const BLANK_FORM = {
  code: "",
  type: "fixed" as "fixed" | "percentage" | "trial",
  amount: "",
  trialDays: "",
  description: "",
  maxUses: "",
  expiresAt: "",
  isActive: true,
  firstTimeOnly: false,
  scopeCities: [] as string[],
  scopePrices: [] as number[],
};

const DiscountsPage = () => {
  const [codes, setCodes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [codeSearch, setCodeSearch] = useState("");
  const [codePage, setCodePage] = useState(1);
  const [form, setForm] = useState(BLANK_FORM);
  const [createStatus, setCreateStatus] = useState<{ msg: string; ok: boolean } | null>(null);
  const [createLoading, setCreateLoading] = useState(false);

  // Edit modal
  const [editCode, setEditCode] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({ isActive: true });
  const [editStatus, setEditStatus] = useState<{ msg: string; ok: boolean } | null>(null);
  const [editLoading, setEditLoading] = useState(false);

  // Delete
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const res = await adminGetDiscountCodes();
      setCodes(res.data);
    } catch {
      setCodes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCodes(); }, []);

  const isTrial = form.type === "trial";
  const canCreate = !!(
    form.code && form.expiresAt && form.description && form.maxUses &&
    (isTrial ? form.trialDays : form.amount)
  );

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreateLoading(true);
    setCreateStatus(null);
    try {
      await adminCreateDiscountCode({
        code: form.code,
        type: form.type,
        // Trial codes carry no monetary amount; they grant a native free trial.
        amount: isTrial ? 0 : parseFloat(form.amount),
        description: form.description,
        maxUses: parseInt(form.maxUses),
        expiresAt: form.expiresAt,
        isActive: form.isActive,
        firstTimeOnly: form.firstTimeOnly,
        ...(isTrial ? { trialDays: parseInt(form.trialDays) } : {}),
        scopeCities: form.scopeCities.length > 0 ? form.scopeCities : null,
        scopePrices: form.scopePrices.length > 0 ? form.scopePrices : null,
      });
      setCreateStatus({ msg: `Code "${form.code}" created successfully.`, ok: true });
      setForm(BLANK_FORM);
      fetchCodes();
    } catch (e: any) {
      setCreateStatus({ msg: e?.response?.data?.message || "Failed to create code.", ok: false });
    } finally {
      setCreateLoading(false);
    }
  };

  const openEdit = (c: any) => {
    setEditCode(c);
    setEditForm({ isActive: c.is_active });
    setEditStatus(null);
  };

  const handleEdit = async () => {
    if (!editCode) return;
    setEditLoading(true);
    setEditStatus(null);
    try {
      await adminUpdateDiscountCode(editCode.id, { isActive: editForm.isActive });
      setEditStatus({ msg: "Updated successfully.", ok: true });
      fetchCodes();
    } catch (e: any) {
      setEditStatus({ msg: e?.response?.data?.message || "Failed to update.", ok: false });
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (c: any) => {
    if (!window.confirm(`Delete code "${c.code}"? This cannot be undone.`)) return;
    setDeleteLoading(c.id);
    try {
      await adminDeleteDiscountCode(c.id);
      fetchCodes();
    } catch (e: any) {
      alert(e?.response?.data?.message || "Failed to delete.");
    } finally {
      setDeleteLoading(null);
    }
  };

  const visibleCodes = codes
    .filter((c: any) => !(c.description ?? "").toLowerCase().includes("referral"))
    .filter((c: any) => !codeSearch ||
      (c.code ?? "").toLowerCase().includes(codeSearch.toLowerCase()) ||
      (c.description ?? "").toLowerCase().includes(codeSearch.toLowerCase()));

  const totalPages = Math.max(1, Math.ceil(visibleCodes.length / CODES_PER_PAGE));
  const pagedCodes = visibleCodes.slice((codePage - 1) * CODES_PER_PAGE, codePage * CODES_PER_PAGE);

  const handleCodeSearch = (val: string) => { setCodeSearch(val); setCodePage(1); };

  return (
    <>
      <Card>
        <SectionTitle>Create Discount Code</SectionTitle>
        <FormGrid>
          <FormField>
            <Label>Code *</Label>
            <Input placeholder="e.g. SAVE20" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          </FormField>
          <FormField>
            <Label>Type *</Label>
            <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as any })}>
              <option value="fixed">Fixed ($)</option>
              <option value="percentage">Percentage (%)</option>
              <option value="trial">Free trial (days)</option>
            </Select>
          </FormField>
          <FormField>
            <Label>{isTrial ? "Trial days *" : "Amount *"}</Label>
            {isTrial ? (
              <Input type="number" placeholder="e.g. 30" value={form.trialDays} onChange={(e) => setForm({ ...form, trialDays: e.target.value })} />
            ) : (
              <Input type="number" placeholder={form.type === "fixed" ? "e.g. 25" : "e.g. 20"} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            )}
          </FormField>
          <FormField>
            <Label>Max Uses *</Label>
            <Input type="number" placeholder="e.g. 100" value={form.maxUses} onChange={(e) => setForm({ ...form, maxUses: e.target.value })} />
          </FormField>
          <FormField>
            <Label>Expires At *</Label>
            <Input type="date" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
          </FormField>
          <FormField>
            <Label>Description *</Label>
            <Input placeholder="e.g. Spring promo" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </FormField>
        </FormGrid>
        <FormGrid style={{ marginTop: 12 }}>
          <FormField>
            <Label>Limit to cities (optional)</Label>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {CITY_OPTIONS.map(city => (
                <label key={city} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={form.scopeCities.includes(city)}
                    onChange={(e) => setForm({
                      ...form,
                      scopeCities: e.target.checked
                        ? [...form.scopeCities, city]
                        : form.scopeCities.filter(c => c !== city),
                    })}
                  />
                  {city}
                </label>
              ))}
            </div>
          </FormField>
          <FormField>
            <Label>Limit to price tiers (optional)</Label>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {PRICE_OPTIONS.map(price => (
                <label key={price} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={form.scopePrices.includes(price)}
                    onChange={(e) => setForm({
                      ...form,
                      scopePrices: e.target.checked
                        ? [...form.scopePrices, price]
                        : form.scopePrices.filter(p => p !== price),
                    })}
                  />
                  ${price}/mo
                </label>
              ))}
            </div>
          </FormField>
        </FormGrid>
        <Row style={{ marginTop: 12, gap: 24, alignItems: "center" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={form.firstTimeOnly}
              onChange={(e) => setForm({ ...form, firstTimeOnly: e.target.checked })}
            />
            First-time subscribers only
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            Active
          </label>
        </Row>
        <Row style={{ marginTop: 12 }}>
          <Button onClick={handleCreate} disabled={!canCreate || createLoading}>
            {createLoading ? "Creating…" : "Create Code in Stripe + DB"}
          </Button>
        </Row>
        {createStatus && <StatusText success={createStatus.ok}>{createStatus.msg}</StatusText>}
      </Card>

      <Card>
        <SectionTitle>Existing Codes</SectionTitle>
        <Row style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search codes or descriptions…"
            value={codeSearch}
            onChange={(e) => handleCodeSearch(e.target.value)}
            style={{ maxWidth: 480 }}
          />
        </Row>
        {loading ? (
          <EmptyState>Loading…</EmptyState>
        ) : visibleCodes.length === 0 ? (
          <EmptyState>No discount codes found.</EmptyState>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <Table>
                <thead>
                  <tr>
                    <Th>Code</Th>
                    <Th>Description</Th>
                    <Th>Type</Th>
                    <Th>Amount</Th>
                    <Th>Used</Th>
                    <Th>Max Uses</Th>
                    <Th>Expires</Th>
                    <Th>Active</Th>
                    <Th></Th>
                  </tr>
                </thead>
                <tbody>
                  {pagedCodes.map((c: any) => (
                    <tr key={c.id}>
                      <Td><strong>{c.code}</strong></Td>
                      <Td data-label="Description">{c.description || "—"}</Td>
                      <Td data-label="Type">{c.type}</Td>
                      <Td data-label="Amount">{c.type === "trial" ? `${c.trial_days ?? 30}-day trial` : c.type === "fixed" ? formatCurrency(Number(c.amount)) : `${c.amount}%`}</Td>
                      <Td data-label="Used">{c.used_count ?? 0}</Td>
                      <Td data-label="Max uses">{c.max_uses ?? "—"}</Td>
                      <Td data-label="Expires">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}</Td>
                      <Td data-label="Active">
                        <Badge color={c.is_active ? "#dcfce7" : "#fee2e2"}>
                          {c.is_active ? "Yes" : "No"}
                        </Badge>
                      </Td>
                      <Td onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <Button variant="secondary" onClick={() => openEdit(c)} style={{ padding: "4px 10px", fontSize: 12 }}>Edit</Button>
                          <Button
                            variant="danger"
                            onClick={() => handleDelete(c)}
                            disabled={deleteLoading === c.id}
                            style={{ padding: "4px 10px", fontSize: 12 }}
                          >
                            {deleteLoading === c.id ? "…" : "Delete"}
                          </Button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {totalPages > 1 && (
              <Row style={{ marginTop: 16, justifyContent: "center" }}>
                <Button variant="secondary" onClick={() => setCodePage((p) => Math.max(1, p - 1))} disabled={codePage === 1}>← Prev</Button>
                <span style={{ fontSize: 13, color: "#555", alignSelf: "center" }}>Page {codePage} of {totalPages}</span>
                <Button variant="secondary" onClick={() => setCodePage((p) => Math.min(totalPages, p + 1))} disabled={codePage === totalPages}>Next →</Button>
              </Row>
            )}
          </>
        )}
      </Card>

      {editCode && (
        <ModalOverlay onClick={() => setEditCode(null)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Edit — {editCode.code}</ModalTitle>
              <CloseButton onClick={() => setEditCode(null)}>×</CloseButton>
            </ModalHeader>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", marginBottom: 20 }}>
              <input
                type="checkbox"
                checked={editForm.isActive}
                onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
              />
              Active (synced to Stripe)
            </label>
            <Row>
              <Button onClick={handleEdit} disabled={editLoading}>
                {editLoading ? "Saving…" : "Save Changes"}
              </Button>
              <Button variant="secondary" onClick={() => setEditCode(null)}>Cancel</Button>
            </Row>
            {editStatus && <StatusText success={editStatus.ok}>{editStatus.msg}</StatusText>}
          </Modal>
        </ModalOverlay>
      )}
    </>
  );
};

export default DiscountsPage;
